import useLocalStorage from '@rehooks/local-storage';
import { useEnvironmentBanner } from '../../hooks/useEnvironmentBanner';
import { useIdentity } from '../../hooks/useIdentity';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useEolBarMessage } from '../../useEolBarMessage';
import { useIshBarMessage } from '../../useIshBarMessage';
import { ZuoraRevenueStorageKeys } from '../../hooks/useRevenueNavigation';
import { useCurrentLocation } from '../../hooks/useCurrentLocation';
import { useVariant, Variant } from './useVariant';
import { useRevenueProfiles } from '../../hooks/useRevenueProfiles';

export const ENVBANNER_HEIGHT = 24;
export const OPT_IN_BAR_HEIGHT = 28;

export const useBillingEnvironmentBar = (enabled = true) => {
  const [userPreferences] = useUserPreferences({ enabled });
  const { data: identity } = useIdentity({ enabled });
  const isTenantOnDenyList = (
    (window as any).zReinventedDenyList || []
  ).includes(identity?.defaultTenantId);
  const { showEnvironmentBanner } = useEnvironmentBanner({ enabled });
  const DISABLED_PATHS_OPT_OUT_BANNER = [
    '/platform/apps/builder/',
    '/platform/apps/meters/configure/',
  ];

  const pathname = window.location.pathname;

  const validPath = !DISABLED_PATHS_OPT_OUT_BANNER.some((disabledPath) =>
    pathname.startsWith(disabledPath),
  );

  const showOptInBar =
    !isTenantOnDenyList && !(userPreferences?.reinvented ?? true) && validPath;

  const EolBarMessage = useEolBarMessage(
    pathname,
    window.location.search,
    enabled,
  );

  const { message: IshOnlyBarMessage, hideIshBar } = useIshBarMessage(
    pathname,
    window.location.search,
    enabled,
  );

  const totalBarHeight =
    (showEnvironmentBanner ? ENVBANNER_HEIGHT : 0) +
    (showOptInBar ? OPT_IN_BAR_HEIGHT : 0) +
    (EolBarMessage ? ENVBANNER_HEIGHT : 0) +
    (IshOnlyBarMessage ? ENVBANNER_HEIGHT : 0);

  return {
    showEnvironmentBanner,
    showOptInBar,
    EolBarMessage,
    IshOnlyBarMessage,
    hideIshBar,
    totalBarHeight,
  };
};

export const useRevenueEnvironmentBar = (enabled = true) => {
  const { host } = useCurrentLocation();
  const [tenantEnv] = useLocalStorage(ZuoraRevenueStorageKeys.TenantEnv) || '';
  const env = host.includes('one.stg') ? 'STAGING' : tenantEnv;
  const variant = useVariant();
  const { data: disableTenantBanner } = useRevenueProfiles(
    'DISABLE_TENANT_BANNER',
    variant === Variant.ONE_ID_REVENUE && enabled,
  );
  if (
    variant === Variant.ONE_ID_REVENUE &&
    disableTenantBanner === 'N' &&
    env
  ) {
    return {
      totalBarHeight: ENVBANNER_HEIGHT,
    };
  }
  return {
    totalBarHeight: 0,
  };
};

export const useEnvironmentBar = () => {
  const variant = useVariant();
  const isBilling = variant === Variant.BILLING;
  const isOneIdRevenue = variant === Variant.ONE_ID_REVENUE;
  const { totalBarHeight: billingTotalBarHeight } =
    useBillingEnvironmentBar(isBilling);
  const { totalBarHeight: revenueTotalBarHeight } =
    useRevenueEnvironmentBar(isOneIdRevenue);

  if (isBilling) {
    return { totalBarHeight: billingTotalBarHeight };
  }
  if (variant === Variant.ONE_ID_REVENUE) {
    return { totalBarHeight: revenueTotalBarHeight };
  }

  return {
    totalBarHeight: 0,
  };
};
